import { useMemo } from 'react';

import {
  BoxIcon,
  GoalIcon,
  InfoIcon,
  type LucideIcon,
  MessageSquareIcon,
  ZapIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { TruncatedText } from '@/components/atoms/TruncatedText/TruncatedText';
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import useBrains from '@/hooks/useBrains';
import useDesks from '@/hooks/useDesks';
import { cn } from '@/lib/utils';
import { getChannelIcon } from '@/modules/campaigns/components/Tables/CampaignsTable/ChannelCell';
import { CallRecordingPlayer } from '@/modules/tryIt/components/CallRecordingPlayer';
import { useCallRecordingForSession } from '@/modules/tryIt/components/ModalTryIt/useCallRecordingForSession';
import { useLeaveModal } from '@/modules/tryIt/hooks/useLeaveModal';
import { useSessionContent } from '@/modules/tryIt/hooks/useSessionContent';
import { useSessionMeaningful } from '@/modules/tryIt/hooks/useSessionMeaningful';
import {
  selectSelectedTurn,
  selectSessionCampaign,
  selectSessionStart,
  selectTryItSessionId,
} from '@/modules/tryIt/redux/selectors';
import { InfoRow, InfoTextRow, PanelSectionTitle } from '@/modules/tryIt/shared/InfoRow';
import { TagChips } from '@/modules/tryIt/shared/TagChips';
import { selectAccountSlug } from '@/redux/session/selectors';
import { LogSessionContentRow } from '@/shared/types/analytics';
import { IntegrationType } from '@/shared/types/integration';
import { getChannelKey } from '@/util/channelName';
import { formatDate, formatSecondsToMmSs, getSecondsDifference } from '@/util/dateUtils';
import { onPlainClick } from '@/util/events';

const VOICE_CHANNELS: IntegrationType[] = [
  IntegrationType.TWILIO_VOICE,
  IntegrationType.TELNYX_VOICE,
  IntegrationType.WEB_VOICE,
];

/** A call continued on web chat is stored as web chat, so the voice event owns the recording. */
export const resolveVoiceOrigin = (content?: LogSessionContentRow) => {
  if (!content) {
    return null;
  }
  if (VOICE_CHANNELS.includes(content.channel) && content.integration_id) {
    return { channel: content.channel, integrationId: content.integration_id };
  }
  const voiceEvent = content.messages?.find(
    (message) =>
      message.channel && VOICE_CHANNELS.includes(message.channel) && message.integration_id
  );
  if (!voiceEvent?.channel || !voiceEvent.integration_id) {
    return null;
  }
  return { channel: voiceEvent.channel, integrationId: voiceEvent.integration_id };
};

/** Containment / coverage / meaningfulness read as an outcome: green when met, amber when not. */
const outcomeClass = (met: boolean) => cn('font-semibold', met ? 'text-success' : 'text-warning');

const RowIcon = ({ icon: Icon }: { icon: LucideIcon }) => (
  <Icon size={16} className="shrink-0 text-muted-foreground" />
);

/** In-modal link: the modal has to close before the route change is visible. */
const PanelLink = ({ to, children }: { to: string; children: React.ReactNode }) => {
  const leaveModal = useLeaveModal();

  return (
    <a
      href={to}
      onClick={onPlainClick(() => leaveModal(to))}
      className="flex min-w-0 items-center gap-1 text-sm leading-5 font-medium text-primary hover:underline"
    >
      {children}
    </a>
  );
};

const PanelValue = ({ children }: { children: React.ReactNode }) => (
  <span className="flex min-w-0 items-center gap-1">{children}</span>
);

/** Read-only Conversation tab for replay — session ids, origin and metrics. */
export const ConversationPanelReplay = () => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const selectedTurn = useSelector(selectSelectedTurn);
  const campaign = useSelector(selectSessionCampaign);
  const sessionId = useSelector(selectTryItSessionId);
  const sessionStart = useSelector(selectSessionStart);
  const { data: content, isLoading } = useSessionContent(sessionId, sessionStart);
  const isMeaningful = useSessionMeaningful(sessionId, content?.start_time);
  const { desks, isLoadingDesks } = useDesks();
  const { brains } = useBrains();
  const brainNameById = useMemo(
    () => new Map((brains ?? []).map((brain) => [brain.brain_id, brain.name])),
    [brains]
  );

  const voiceOrigin = resolveVoiceOrigin(content);
  const { recording, showRecordingPlayer, recordingUrl } = useCallRecordingForSession(
    voiceOrigin
      ? ({
          ...content,
          channel: voiceOrigin.channel,
          integration_id: voiceOrigin.integrationId,
        } as LogSessionContentRow)
      : content,
    !!voiceOrigin
  );

  if (isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }

  if (!content) {
    return (
      <Empty className="justify-start px-0 py-12">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <MessageSquareIcon aria-hidden />
          </EmptyMedia>
          <EmptyTitle>{t('try_it.conversation.unavailable')}</EmptyTitle>
        </EmptyHeader>
      </Empty>
    );
  }

  const startedAt = content.start_time
    ? `${formatDate(content.start_time, 'MMM d, yyyy')} · ${formatDate(content.start_time, 'hh:mm a')}`
    : null;
  const duration =
    content.start_time && content.end_time
      ? formatSecondsToMmSs(getSecondsDifference(content.end_time, content.start_time))
      : null;
  // The start→expiry span, not the raw expiry timestamp.
  const expiredAfter =
    content.start_time && content.expired_time
      ? formatSecondsToMmSs(getSecondsDifference(content.expired_time, content.start_time))
      : null;
  const participatedBrains = content.participated_brains ?? [];
  const tags = content.tags ?? [];
  const deskName = desks?.find((desk) => desk.desk_id === content.desk_id)?.name;
  const environmentLabel =
    deskName ??
    (content.is_test ? t('try_it.conversation.preview') : t('try_it.conversation.production'));
  const meaningfulTooltip =
    content.is_test && !isMeaningful
      ? t('try_it.conversation.non_meaningful_preview_tooltip')
      : t('analytics.meaningful_tooltip');

  // With a subscriber id the link lands on that subscriber's journey; without one,
  // on the campaign logs filtered by the end user.
  const campaignLink = (() => {
    if (!campaign || !slug) {
      return null;
    }
    const base = `/${slug}/campaigns/${campaign.id}/logs`;
    if (campaign.subscriberId) {
      return `${base}/${campaign.subscriberId}`;
    }
    const term = content.channel_user_id ?? content.user_email;
    return term ? `${base}?q=${encodeURIComponent(term)}` : base;
  })();

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-col gap-4">
        <InfoRow label={t('try_it.conversation.containment')}>
          <span className={outcomeClass(content.is_contained)}>
            {content.is_contained ? t('try_it.modal.contained') : t('try_it.modal.not_contained')}
          </span>
        </InfoRow>
        <InfoRow label={t('try_it.conversation.coverage')}>
          <span className={outcomeClass(content.is_covered)}>
            {content.is_covered ? t('try_it.modal.covered') : t('try_it.modal.not_covered')}
          </span>
        </InfoRow>
        {isMeaningful !== undefined && (
          <InfoRow label={t('try_it.conversation.meaningfulness')}>
            <span className="flex min-w-0 items-center gap-1.5">
              <span className={outcomeClass(isMeaningful)}>
                {isMeaningful
                  ? t('try_it.conversation.meaningful')
                  : t('try_it.conversation.non_meaningful')}
              </span>
              <InfoTooltip content={meaningfulTooltip} size="large" side="right">
                <InfoIcon className="size-4 shrink-0 text-muted-foreground" />
              </InfoTooltip>
            </span>
          </InfoRow>
        )}
        <InfoTextRow label={t('try_it.conversation.conversation_id')} value={content.session_id} />
        <InfoTextRow label={t('try_it.conversation.request_id')} value={selectedTurn?.requestId} />
      </div>

      {/* `my-2` on top of the column's gap-4 = 24px around every divider. */}
      <Separator className="my-2" />

      <div className="flex flex-col gap-4">
        <InfoRow label={t('try_it.conversation.environment')}>
          {/* Skeleton while the desk list loads, or the fallback label would show
              first and then be replaced by the desk name. */}
          {content.desk_id && isLoadingDesks ? (
            <Skeleton className="h-5 w-32" />
          ) : (
            <>
              {content.desk_id && slug ? (
                <PanelLink to={`/${slug}/environments/${content.desk_id}`}>
                  <RowIcon icon={BoxIcon} />
                  <TruncatedText text={environmentLabel} size="large" />
                </PanelLink>
              ) : (
                <PanelValue>
                  <RowIcon icon={BoxIcon} />
                  <TruncatedText text={environmentLabel} size="large" />
                </PanelValue>
              )}
            </>
          )}
        </InfoRow>
        {content.channel && (
          <InfoRow label={t('try_it.conversation.channel')}>
            <PanelValue>
              {/* `getChannelIcon`, not `integrationIconByType`: it falls back to a
                  generic glyph for channels with no brand icon. */}
              <span className="flex shrink-0 items-center">
                {getChannelIcon(content.channel, 16)}
              </span>
              <TruncatedText
                text={t(getChannelKey(content.channel))}
                className="text-sm leading-5 font-normal text-foreground"
                size="large"
              />
            </PanelValue>
          </InfoRow>
        )}
        {campaign && (
          <InfoRow label={t('try_it.conversation.campaign')}>
            {campaignLink ? (
              <PanelLink to={campaignLink}>
                <RowIcon icon={GoalIcon} />
                <TruncatedText text={campaign.name ?? campaign.id} size="large" />
              </PanelLink>
            ) : (
              <PanelValue>
                <RowIcon icon={GoalIcon} />
                <TruncatedText text={campaign.name ?? campaign.id} size="large" />
              </PanelValue>
            )}
          </InfoRow>
        )}
        {participatedBrains.length > 0 && (
          <InfoRow label={t('try_it.conversation.ai_agents', { count: participatedBrains.length })}>
            <div className="flex min-w-0 flex-col gap-1">
              {participatedBrains.map((brain) => {
                const name = brainNameById.get(brain.brain_parent_id);
                const version =
                  brain.brain_version === 0 ? t('common.draft') : `v${brain.brain_version}`;
                const label = name ? `${name} [${version}]` : t('analytics.deleted_ai_agent');
                const to = slug ? `/${slug}/ai-agents/${brain.brain_parent_id}` : null;

                if (!name || !to) {
                  return (
                    <PanelValue key={brain.brain_id}>
                      <RowIcon icon={ZapIcon} />
                      <TruncatedText text={label} size="large" />
                    </PanelValue>
                  );
                }
                return (
                  <PanelLink key={brain.brain_id} to={to}>
                    <RowIcon icon={ZapIcon} />
                    <TruncatedText text={label} size="large" />
                  </PanelLink>
                );
              })}
            </div>
          </InfoRow>
        )}
      </div>

      <Separator className="my-2" />

      <div className="flex flex-col gap-4">
        <InfoRow label={t('try_it.conversation.user_messages')}>
          {content.total_user_messages}
        </InfoRow>
        {startedAt && (
          <InfoRow label={t('try_it.conversation.session_started')}>{startedAt}</InfoRow>
        )}
        {duration && <InfoRow label={t('try_it.conversation.duration')}>{duration}</InfoRow>}
        {expiredAfter && (
          <InfoRow label={t('try_it.conversation.expired_after')}>{expiredAfter}</InfoRow>
        )}
        {tags.length > 0 && (
          <InfoRow label={t('try_it.conversation.tags')}>
            <TagChips tags={tags} />
          </InfoRow>
        )}
      </div>

      {showRecordingPlayer && recording && recordingUrl && (
        <>
          <Separator className="my-2" />
          <div className="flex flex-col gap-3">
            <PanelSectionTitle>{t('try_it.call_recording')}</PanelSectionTitle>
            <CallRecordingPlayer recording={recording} recordingUrl={recordingUrl} />
          </div>
        </>
      )}
    </div>
  );
};
